import React from "react";
import { Modal, Table, Row, Col, Typography, Divider, Card, Statistic, Tag, Empty } from "antd";
import {
  ArrowUpOutlined,
  ArrowDownOutlined,
  DollarOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";

const { Title, Text } = Typography;

const ReportDetails = ({ visible, onClose, report }) => {
  if (!report || !report.report_data) return null;

  const data = report.report_data;

  // Support both new step-based format and legacy format
  const isNewFormat = data.step_1_close_revenue !== undefined;

  if (!isNewFormat) {
    return <LegacyReport visible={visible} onClose={onClose} report={report} />;
  }

  const step1 = data.step_1_close_revenue || {};
  const step2 = data.step_2_close_expenses || {};
  const step3 = data.step_3_close_income_summary || {};
  const step4 = data.step_4_close_drawings || {};
  const capitalMovement = data.capital_movement || {};
  const balanceSheet = data.balance_sheet || [];

  const journalColumns = [
    {
      title: "Account Code",
      dataIndex: "code",
      key: "code",
      width: 120,
    },
    {
      title: "Account Name",
      dataIndex: "account",
      key: "account",
    },
    {
      title: "Debit (৳)",
      dataIndex: "debit",
      key: "debit",
      align: "right",
      render: (text) =>
        text ? (
          <Text strong style={{ color: "#1890ff" }}>
            {parseFloat(text).toLocaleString("en-BD", { minimumFractionDigits: 2 })}
          </Text>
        ) : (
          ""
        ),
    },
    {
      title: "Credit (৳)",
      dataIndex: "credit",
      key: "credit",
      align: "right",
      render: (text) =>
        text ? (
          <Text strong style={{ color: "#52c41a" }}>
            {parseFloat(text).toLocaleString("en-BD", { minimumFractionDigits: 2 })}
          </Text>
        ) : (
          ""
        ),
    },
  ];

  const balanceColumns = [
    {
      title: "Account Code",
      dataIndex: "code",
      key: "code",
      width: 120,
    },
    {
      title: "Account Name",
      dataIndex: "account",
      key: "account",
    },
    {
      title: "Balance (৳)",
      dataIndex: "balance",
      key: "balance",
      align: "right",
      render: (text) => {
        const val = parseFloat(text || 0);
        return (
          <Text strong type={val >= 0 ? "success" : "danger"}>
            {val.toLocaleString("en-BD", { minimumFractionDigits: 2 })}
          </Text>
        );
      },
    },
  ];

  const totalRevenue = parseFloat(step1.total_revenue || 0);
  const totalExpenses = parseFloat(step2.total_expenses || 0);
  const netProfit = parseFloat(step3.net_profit || 0);
  const totalDrawings = parseFloat(step4.total_drawings || 0);
  const openingCapital = parseFloat(capitalMovement.opening_capital || 0);
  const closingCapital = parseFloat(capitalMovement.closing_capital || 0);

  const formatAmount = (val) =>
    val.toLocaleString("en-BD", { minimumFractionDigits: 2 });

  const renderStepCard = (title, entries, summaryLabel, summaryValue, color) => (
    <Card
      title={<Title level={5} style={{ margin: 0 }}>{title}</Title>}
      style={{ marginBottom: 16 }}
      size="small"
    >
      {entries && entries.length > 0 ? (
        <Table
          dataSource={entries}
          columns={journalColumns}
          rowKey={(record, index) => `${record.code}-${index}`}
          pagination={false}
          size="small"
          bordered
          summary={() => (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={2} align="right">
                <Text strong>{summaryLabel}</Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={1} align="right">
                <Text strong style={{ color }}>
                  ৳{formatAmount(summaryValue)}
                </Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={2} />
            </Table.Summary.Row>
          )}
        />
      ) : (
        <Empty description="No entries for this step" />
      )}
    </Card>
  );

  return (
    <Modal
      title={`Closing Report (Period: ${report.period?.start_date} to ${report.period?.end_date})`}
      visible={visible}
      onCancel={onClose}
      footer={null}
      width={1000}
      style={{ top: 20 }}
      bodyStyle={{ maxHeight: "80vh", overflowY: "auto" }}
    >
      {/* Summary Statistics */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="Total Revenue"
              value={totalRevenue}
              precision={2}
              prefix="৳"
              valueStyle={{ color: "#52c41a", fontSize: 16 }}
              suffix={<ArrowUpOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="Total Expenses"
              value={totalExpenses}
              precision={2}
              prefix="৳"
              valueStyle={{ color: "#ff4d4f", fontSize: 16 }}
              suffix={<ArrowDownOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="Net Profit"
              value={netProfit}
              precision={2}
              prefix="৳"
              valueStyle={{ color: netProfit >= 0 ? "#52c41a" : "#ff4d4f", fontSize: 16 }}
              suffix={netProfit >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="Owner Drawings"
              value={totalDrawings}
              precision={2}
              prefix="৳"
              valueStyle={{ color: "#faad14", fontSize: 16 }}
              suffix={<DollarOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Step 1: Close Revenue */}
      {renderStepCard(
        "Step 1: Close Revenue Accounts → Income Summary",
        step1.entries,
        "Total Revenue",
        totalRevenue,
        "#52c41a"
      )}

      {/* Step 2: Close Expenses */}
      {renderStepCard(
        "Step 2: Close Expense Accounts → Income Summary",
        step2.entries,
        "Total Expenses",
        totalExpenses,
        "#ff4d4f"
      )}

      {/* Step 3: Close Income Summary */}
      <Card
        title={<Title level={5} style={{ margin: 0 }}>Step 3: Close Income Summary → Retained Earnings</Title>}
        style={{ marginBottom: 16 }}
        size="small"
      >
        {step3.entries && step3.entries.length > 0 ? (
          <Table
            dataSource={step3.entries}
            columns={journalColumns}
            rowKey={(record, index) => `${record.code}-${index}`}
            pagination={false}
            size="small"
            bordered
          />
        ) : (
          <Empty description="No entries for this step" />
        )}
        <div style={{ marginTop: 12, textAlign: "right" }}>
          <Tag color={netProfit >= 0 ? "green" : "red"} style={{ fontSize: 14, padding: "4px 12px" }}>
            {netProfit >= 0 ? "Net Profit" : "Net Loss"}: ৳{formatAmount(Math.abs(netProfit))}
          </Tag>
        </div>
      </Card>

      {/* Step 4: Close Drawings */}
      <Card
        title={<Title level={5} style={{ margin: 0 }}>Step 4: Close Owner Drawings → Capital</Title>}
        style={{ marginBottom: 16 }}
        size="small"
      >
        {step4.entries && step4.entries.length > 0 ? (
          <Table
            dataSource={step4.entries}
            columns={journalColumns}
            rowKey={(record, index) => `${record.code}-${index}`}
            pagination={false}
            size="small"
            bordered
          />
        ) : (
          <Empty description="No drawings to close" />
        )}
      </Card>

      <Divider />



      {/* Post-Closing Balance Sheet */}
      <Card
        title={<Title level={5} style={{ margin: 0 }}>Post-Closing Balance Sheet</Title>}
        size="small"
      >
        {balanceSheet.length > 0 ? (
          <Table
            dataSource={balanceSheet}
            columns={balanceColumns}
            rowKey="code"
            pagination={false}
            size="small"
            bordered
          />
        ) : (
          <Empty description="No balance sheet data" />
        )}
      </Card>
    </Modal>
  );
};

// Legacy report format support
const LegacyReport = ({ visible, onClose, report }) => {
  const { "Income Statement": incomeData, "Balance Sheet": balanceData, "Net Income": netIncome } = report.report_data;

  const columns = [
    { title: "Account Code", dataIndex: "code", key: "code" },
    { title: "Account Name", dataIndex: "account", key: "account" },
    {
      title: "Balance",
      dataIndex: "balance",
      key: "balance",
      render: (text) => parseFloat(text).toFixed(2),
    },
  ];

  return (
    <Modal
      title={`Closing Report Details (Period: ${report.period?.start_date} to ${report.period?.end_date})`}
      visible={visible}
      onCancel={onClose}
      footer={null}
      width={800}
    >
      <Row>
        <Col span={24}>
          <Title level={4}>Income Statement Snapshot</Title>
          <Table
            dataSource={incomeData || []}
            columns={columns}
            rowKey="code"
            pagination={false}
            size="small"
            bordered
          />
          <div style={{ marginTop: 10, textAlign: "right" }}>
            <Text strong>Net Income: </Text>
            <Text type={parseFloat(netIncome) >= 0 ? "success" : "danger"}>
              {parseFloat(netIncome || 0).toFixed(2)}
            </Text>
          </div>
        </Col>
      </Row>

      <Divider />

      <Row>
        <Col span={24}>
          <Title level={4}>Balance Sheet Snapshot</Title>
          <Table
            dataSource={balanceData || []}
            columns={columns}
            rowKey="code"
            pagination={false}
            size="small"
            bordered
          />
        </Col>
      </Row>
    </Modal>
  );
};

export default ReportDetails;
